// CartHub Dashboard — PriceAlerts Component.
// Price Alert Stats: KPI, top prodotti, lista paginata; legenda verso Design + WordPress.

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api, ago, Ic, LegendInfoIcon, useMediaMinWidth } from "../shared";
import { dateLocaleForAppLang, formatMoney } from "../i18n";

function PriceAlertStatsLegend({ onOpenSetup }) {
  const { t } = useTranslation("priceAlerts");
  const isDesktop = useMediaMinWidth(900);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!isDesktop) setMobileOpen(false);
  }, [isDesktop]);

  const panelId = "price-alert-stats-legend-panel";
  const showPanel = isDesktop || mobileOpen;

  return (
    <section className="ch-help-legend ch-help-legend--embed" style={{ marginBottom: 20 }} aria-label={t("statsLegend.aria")}>
      <div className="ch-help-legend__accent" aria-hidden="true" />
      {!isDesktop && (
        <button
          type="button"
          className="ch-help-legend__summary"
          aria-expanded={mobileOpen}
          aria-controls={panelId}
          onClick={() => setMobileOpen((o) => !o)}
        >
          <span className="ch-help-legend__sum-icon">
            <LegendInfoIcon size={18} />
          </span>
          <span>
            {t("statsLegend.summaryTitle")}
            <span className="ch-help-legend__sum-hint">{t("statsLegend.summaryHint")}</span>
          </span>
          <span className="ch-help-legend__chev" aria-hidden="true" />
        </button>
      )}
      {showPanel && (
        <div id={panelId} className="ch-help-legend__panel">
          <header className="ch-help-legend__head ch-help-legend__head--mobile-only">
            <div className="ch-help-legend__head-icon" aria-hidden="true">
              <LegendInfoIcon />
            </div>
            <div>
              <h2 className="ch-help-legend__title">{t("statsLegend.summaryTitle")}</h2>
              <p className="ch-help-legend__sub">{t("statsLegend.summaryHint")}</p>
            </div>
          </header>

          <div className="ch-help-legend__desktop">
            <div className="ch-help-legend__desktop-intro">
              <div className="ch-help-legend__head-icon" aria-hidden="true">
                <LegendInfoIcon size={20} />
              </div>
              <div>
                <h2 className="ch-help-legend__title">{t("statsLegend.summaryTitle")}</h2>
                <p className="ch-help-legend__sub">{t("statsLegend.summaryHint")}</p>
              </div>
            </div>
            <div style={{ padding: "14px 18px", fontSize: 14, lineHeight: 1.55 }}>
              <p className="ch-help-legend__text" style={{ margin: "0 0 12px" }}>
                {t("statsLegend.body")}
              </p>
              {typeof onOpenSetup === "function" ? (
                <button type="button" className="pill" style={{ cursor: "pointer", border: "1px solid #e5e5e5" }} onClick={onOpenSetup}>
                  {t("statsLegend.linkSetup")}
                </button>
              ) : null}
            </div>
          </div>

          <div className="ch-help-legend__list" style={{ paddingTop: 8 }}>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--neutral" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <p className="ch-help-legend__text">{t("statsLegend.body")}</p>
                {typeof onOpenSetup === "function" ? (
                  <button type="button" className="pill" style={{ cursor: "pointer", border: "1px solid #e5e5e5", marginTop: 10 }} onClick={onOpenSetup}>
                    {t("statsLegend.linkSetup")}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// API: GET /api/price-alerts/:shopId/stats | GET …/:shopId?… lista |
//      GET /api/price-alerts/:shopId/:alertId — dettaglio + cronologia email PA
export default function PriceAlerts({ shopId, onOpenSetup, shopCurrency = "EUR" }) {
  const { t, i18n } = useTranslation("priceAlerts");
  const { t: tc } = useTranslation("carts");
  const dateLoc = dateLocaleForAppLang(i18n.language);
  const paMoney = (n, cur) => formatMoney(i18n.language, cur || shopCurrency || "EUR", n);
  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString(dateLoc, { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtSent = (d) =>
    d
      ? new Date(d).toLocaleString(dateLoc, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
      : "—";

  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const PER_PAGE = 20;

  useEffect(() => {
    if (!shopId) return;
    api(`/price-alerts/${shopId}/stats`).then((d) => setStats(d)).catch(() => {});
  }, [shopId]);

  useEffect(() => {
    if (!shopId) return;
    setLoading(true);
    const qs = `/price-alerts/${shopId}?limit=${PER_PAGE}&offset=${page * PER_PAGE}${statusFilter ? `&status=${statusFilter}` : ""}`;
    api(qs)
      .then((d) => {
        setAlerts(d.alerts || []);
        setTotal(d.total || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [shopId, page, statusFilter]);

  useEffect(() => {
    if (!shopId || !detailId) {
      setDetailRow(null);
      return;
    }
    setDetailLoading(true);
    setDetailRow(null);
    api(`/price-alerts/${shopId}/${detailId}`)
      .then((row) => setDetailRow(row))
      .catch(() => setDetailRow(null))
      .finally(() => setDetailLoading(false));
  }, [shopId, detailId]);

  const maskEmail = (e) => {
    if (!e) return "—";
    const [u, d] = e.split("@");
    return u.slice(0, 2) + "***@" + d;
  };
  const totalPages = Math.ceil(total / PER_PAGE);

  const statusLabel = (s) => {
    const known = ["active", "pa1_sent", "pa2_sent", "pa3_sent", "poe_sent", "converted", "expired"];
    const label = known.includes(s) ? t(`status.${s}`) : s;
    const mapCls = {
      active: "pa-active",
      pa1_sent: "pa-sent",
      pa2_sent: "pa-sent",
      pa3_sent: "pa-sent",
      poe_sent: "pa-sent",
      converted: "pa-converted",
      expired: "pa-expired",
    };
    const cls = mapCls[s] || "neutral";
    return (
      <span className={`ch-status ${cls}`}>
        <span className="status-dot" />
        {label}
      </span>
    );
  };

  if (!shopId) return null;

  if (!stats && loading)
    return (
      <div>
        <PriceAlertStatsLegend onOpenSetup={onOpenSetup} />
        <div className="empty">
          <div className="empty-icon-svg" aria-hidden>
            {Ic.loader}
          </div>
          <div className="empty-title">{t("loading")}</div>
        </div>
      </div>
    );

  const emailTypeLabel = (tp) => {
    const key = `emailTypes.${tp}`;
    const tr = t(key);
    return tr === key ? tp : tr;
  };

  return (
    <div>
      {detailId ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="pa-detail-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 400,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "24px 16px",
            overflowY: "auto",
          }}
          onClick={() => setDetailId(null)}
        >
          <div
            className="ch-card"
            style={{
              maxWidth: 560,
              width: "100%",
              marginTop: 32,
              marginBottom: 32,
              position: "relative",
              cursor: "default",
            }}
            onClick={(ev) => ev.stopPropagation()}
          >
            <button
              type="button"
              className="ch-btn ch-btn-ghost ch-btn-sm"
              style={{ position: "absolute", top: 12, right: 12 }}
              onClick={() => setDetailId(null)}
              aria-label={t("detailModal.close")}
            >
              ✕
            </button>
            <h2 id="pa-detail-title" style={{ margin: "0 0 12px", fontSize: 17, paddingRight: 36 }}>
              {t("detailModal.title")}
            </h2>
            {detailLoading ? (
              <div style={{ padding: 24, color: "#6b7280" }}>{t("loadingList")}</div>
            ) : !detailRow ? (
              <div style={{ padding: 16, color: "#b91c1c" }}>{t("detailModal.loadError")}</div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                  <div>
                    <strong>{t("detailModal.email")}</strong>{" "}
                    <span style={{ fontFamily: "monospace" }}>{detailRow.customer_email}</span>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <strong>{t("detailModal.product")}</strong> {detailRow.product_name}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    {statusLabel(detailRow.status)}
                  </div>
                </div>
                <hr className="ch-divider" style={{ margin: "12px 0 16px" }} />
                <h3 style={{ margin: "0 0 10px", fontSize: 15 }}>{t("detailModal.emailsTitle")}</h3>
                {(detailRow.emails_sent || []).length === 0 ? (
                  <div className="ch-info">{t("detailModal.noEmails")}</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {(detailRow.emails_sent || []).map((e, idx) => (
                      <div
                        key={idx}
                        style={{ padding: 12, background: "#f9fafb", borderRadius: 6, border: "1px solid #e5e5e5" }}
                      >
                        <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 4 }}>{emailTypeLabel(e.type)}</div>
                        {e.subject ? (
                          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 6, fontStyle: "italic" }}>
                            &quot;{e.subject}&quot;
                          </div>
                        ) : null}
                        {(e.offer_percent != null && e.offer_percent !== "") ||
                        (e.offer_fixed_eur != null && e.offer_fixed_eur !== "") ? (
                          <div style={{ fontSize: 13, color: "#374151", marginBottom: 6, lineHeight: 1.45 }}>
                            {e.offer_percent != null &&
                              e.offer_percent !== "" &&
                              Number.isFinite(Number(e.offer_percent)) &&
                              Number(e.offer_percent) > 0 && (
                                <span style={{ display: "block" }}>
                                  {tc("detail.emailOfferStrategicPct", {
                                    pct: Number(e.offer_percent).toLocaleString(i18n.language, {
                                      maximumFractionDigits: 2,
                                    }),
                                  })}
                                </span>
                              )}
                            {e.offer_fixed_eur != null &&
                              e.offer_fixed_eur !== "" &&
                              Number.isFinite(Number(e.offer_fixed_eur)) &&
                              Number(e.offer_fixed_eur) > 0 && (
                                <span style={{ display: "block" }}>
                                  {tc("detail.emailOfferFixedEur", {
                                    amount: paMoney(e.offer_fixed_eur, detailRow.currency || shopCurrency),
                                  })}
                                </span>
                              )}
                          </div>
                        ) : null}
                        <div style={{ display: "flex", gap: 8, fontSize: 13 }}>
                          <span className="ch-status neutral">{ago(e.sent_at)}</span>
                          {e.opened && <span className="ch-status success">{tc("detail.opened")}</span>}
                          {e.clicked && <span className="ch-status success">{tc("detail.clicked")}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : null}

      <PriceAlertStatsLegend onOpenSetup={onOpenSetup} />
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">{t("kpi.activeSubs")}</div>
          <div className="stat-val">{stats?.active ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t("kpi.pa1Sent")}</div>
          <div className="stat-val">{stats?.pa1_sent ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t("kpi.pa2Sent")}</div>
          <div className="stat-val">{stats?.pa2_sent ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t("kpi.pa3Sent")}</div>
          <div className="stat-val">{stats?.pa3_sent ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t("kpi.poeSent")}</div>
          <div className="stat-val">{stats?.poe_sent ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t("kpi.converted")}</div>
          <div className="stat-val" style={{ color: "#2e7d32" }}>
            {stats?.converted ?? 0}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t("kpi.conversionRate")}</div>
          <div className="stat-val">{stats?.conversion_rate ?? 0}%</div>
        </div>
      </div>

      {stats?.top_products?.length > 0 && (
        <div className="section-card" style={{ marginTop: 20 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 600 }}>{t("topProducts.title")}</h3>
          <div className="table-wrap table-min-wide">
            <table className="ch-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>{t("topProducts.thProduct")}</th>
                  <th>{t("topProducts.thPrice")}</th>
                  <th>{t("topProducts.thSubs")}</th>
                </tr>
              </thead>
              <tbody>
                {stats.top_products.map((p, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: "left", fontWeight: 500 }}>{p.product_name}</td>
                    <td>{paMoney(p.product_price, p.currency)}</td>
                    <td>
                      <span className="nav-badge" style={{ background: "#e1f5fe", color: "#0277bd" }}>
                        {p.count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {[
          ["", "filters.all"],
          ["active", "filters.active"],
          ["pa1_sent", "filters.pa1"],
          ["pa2_sent", "filters.pa2"],
          ["pa3_sent", "filters.pa3"],
          ["poe_sent", "filters.poe"],
          ["converted", "filters.converted"],
          ["expired", "filters.expired"],
        ].map(([v, tk]) => (
          <button
            key={v}
            className={`pill${statusFilter === v ? " green" : ""}`}
            style={{ cursor: "pointer", border: "1px solid #e5e5e5" }}
            onClick={() => {
              setStatusFilter(v);
              setPage(0);
            }}
          >
            {t(tk)}
          </button>
        ))}
      </div>

      <div className="section-card" style={{ marginTop: 12 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 20, color: "#9ca3af" }}>{t("loadingList")}</div>
        ) : alerts.length === 0 ? (
          <div className="empty">
            <div className="empty-icon-svg" aria-hidden>
              {Ic.bell}
            </div>
            <div className="empty-title">{t("empty.title")}</div>
            <div className="empty-sub">{t("empty.sub")}</div>
          </div>
        ) : (
          <>
            <div className="table-wrap table-min-wide">
              <table className="ch-table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>{t("table.thEmail")}</th>
                    <th style={{ textAlign: "left" }}>{t("table.thProduct")}</th>
                    <th>{t("table.thPrice")}</th>
                    <th>{t("table.thStatus")}</th>
                    <th>{t("table.thSub")}</th>
                    <th>{t("table.thPa1")}</th>
                    <th>{t("table.thPa2")}</th>
                    <th>{t("table.thPa3")}</th>
                    {statusFilter === "converted" && <th>{t("table.thConversion")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((a) => (
                    <tr
                      key={a.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setDetailId(a.id)}
                      title={t("detailModal.rowHint")}
                    >
                      <td style={{ textAlign: "left", fontFamily: "monospace", fontSize: 13 }}>{maskEmail(a.customer_email)}</td>
                      <td style={{ textAlign: "left", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.product_name}</td>
                      <td>
                        {paMoney(a.product_price, a.currency)}
                        {a.price_dropped_to && (
                          <div style={{ fontSize: 11, color: "#2e7d32" }}>
                            ↓ {paMoney(a.price_dropped_to, a.currency)}
                          </div>
                        )}
                      </td>
                      <td>{statusLabel(a.status)}</td>
                      <td style={{ fontSize: 13, color: "#6b7280" }}>{fmtDate(a.created_at)}</td>
                      <td style={{ fontSize: 12, color: "#6b7280" }}>{fmtSent(a.pa1_sent_at)}</td>
                      <td style={{ fontSize: 12, color: "#6b7280" }}>{fmtSent(a.pa2_sent_at)}</td>
                      <td style={{ fontSize: 12, color: "#6b7280" }}>{fmtSent(a.pa3_sent_at)}</td>
                      {statusFilter === "converted" && <td style={{ fontSize: 13, color: "#2e7d32" }}>{fmtDate(a.converted_at)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
                <button className="pill" disabled={page === 0} style={{ cursor: page === 0 ? "default" : "pointer", opacity: page === 0 ? 0.5 : 1 }} onClick={() => setPage((p) => p - 1)}>
                  {t("pagination.prev")}
                </button>
                <span style={{ fontSize: 13, color: "#6b7280", alignSelf: "center" }}>
                  {page + 1} / {totalPages}
                </span>
                <button
                  className="pill"
                  disabled={page >= totalPages - 1}
                  style={{ cursor: page >= totalPages - 1 ? "default" : "pointer", opacity: page >= totalPages - 1 ? 0.5 : 1 }}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t("pagination.next")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
